import { useMemo, useState, useEffect } from 'react'
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet'
import MarkerClusterGroup from 'react-leaflet-cluster'
import { divIcon } from 'leaflet'
import PropTypes from 'prop-types'
import 'leaflet/dist/leaflet.css'

/**
 * Education Facility Map Component
 *
 * Interactive map showing education institutions (schools) for the current location
 */
function EducationFacilityMap({ facilities, location, title }) {
  const [selectedSchoolId, setSelectedSchoolId] = useState(null)

  // Find the selected school
  const selectedSchool = useMemo(() => {
    if (!selectedSchoolId || !facilities) return null
    return facilities.find(f => f.institution_id === selectedSchoolId)
  }, [selectedSchoolId, facilities])

  // Close modal on escape key and manage body overflow
  useEffect(() => {
    const handleEscape = e => {
      if (e.key === 'Escape') {
        setSelectedSchoolId(null)
      }
    }

    if (selectedSchoolId) {
      const originalOverflow = document.body.style.overflow
      document.body.style.overflow = 'hidden'
      document.addEventListener('keydown', handleEscape)

      return () => {
        document.removeEventListener('keydown', handleEscape)
        document.body.style.overflow = originalOverflow
      }
    } else {
      document.body.style.overflow = ''
    }
  }, [selectedSchoolId])

  // Safety cleanup on unmount
  useEffect(() => {
    return () => {
      document.body.style.overflow = ''
    }
  }, [])

  // Calculate center and bounds from schools
  const { center, bounds } = useMemo(() => {
    if (!facilities || facilities.length === 0) {
      return {
        center: [1.3733, 32.2903], // Uganda center
        bounds: null,
      }
    }

    const validSchools = facilities.filter(
      f =>
        f.latitude && f.longitude && !isNaN(f.latitude) && !isNaN(f.longitude)
    )

    if (validSchools.length === 0) {
      return {
        center: [1.3733, 32.2903],
        bounds: null,
      }
    }

    const lats = validSchools.map(f => f.latitude)
    const lons = validSchools.map(f => f.longitude)

    const centerLat = (Math.max(...lats) + Math.min(...lats)) / 2
    const centerLon = (Math.max(...lons) + Math.min(...lons)) / 2

    return {
      center: [centerLat, centerLon],
      bounds: [
        [Math.min(...lats), Math.min(...lons)],
        [Math.max(...lats), Math.max(...lons)],
      ],
    }
  }, [facilities])

  // Create marker icon for education institutions
  const createMarkerIcon = institutionType => {
    const typeConfig = {
      Primary: { color: '#3b82f6', size: 22, emoji: '🏫' },
      Secondary: { color: '#10b981', size: 26, emoji: '🎓' },
      Tertiary: { color: '#8b5cf6', size: 28, emoji: '🎯' },
    }

    const config = typeConfig[institutionType] || typeConfig['Primary']

    return divIcon({
      className: 'custom-marker-icon',
      html: `
        <div style="
          background-color: ${config.color};
          width: ${config.size}px;
          height: ${config.size}px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 2px solid white;
          box-shadow: 0 2px 4px rgba(0,0,0,0.3);
          font-size: ${config.size * 0.6}px;
        ">
          ${config.emoji}
        </div>
      `,
      iconSize: [config.size, config.size],
      iconAnchor: [config.size / 2, config.size / 2],
    })
  }

  // Create cluster icon
  const createClusterIcon = cluster => {
    const count = cluster.getChildCount()
    return divIcon({
      html: `<div class="cluster-marker"><span class="cluster-count">${count}</span></div>`,
      className: 'custom-cluster-icon',
      iconSize: [35, 35],
    })
  }

  const validSchools = facilities.filter(
    f => f.latitude && f.longitude && !isNaN(f.latitude) && !isNaN(f.longitude)
  )

  if (validSchools.length === 0) {
    return (
      <div className="bg-white p-6 rounded-lg border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
        <p className="text-gray-600 text-sm">
          No schools with location coordinates available for this area.
        </p>
      </div>
    )
  }

  return (
    <div className="bg-white p-6 rounded-lg border border-gray-200">
      <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
      <p className="text-sm text-gray-600 mb-4">
        Interactive map showing {validSchools.length} school
        {validSchools.length === 1 ? '' : 's'} in{' '}
        {location?.name || 'this location'}
      </p>

      {/* Map Container */}
      <div className="h-96 rounded-lg overflow-hidden border border-gray-200">
        <MapContainer
          center={center}
          zoom={bounds ? undefined : 10}
          bounds={bounds}
          style={{ height: '100%', width: '100%' }}
          className="leaflet-container"
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />

          <MarkerClusterGroup
            iconCreateFunction={createClusterIcon}
            chunkedLoading
            maxClusterRadius={60}
          >
            {validSchools.map((school, index) => (
              <Marker
                key={index}
                position={[school.latitude, school.longitude]}
                icon={createMarkerIcon(school.institution_type)}
              >
                <Popup className="custom-popup" maxWidth={300}>
                  <div className="min-w-48">
                    <h3 className="font-medium text-gray-900 mb-1 capitalize">
                      {school.institution_name}
                    </h3>
                    <div className="space-y-1 text-sm">
                      <div className="flex items-center">
                        <span className="font-medium text-gray-700">Type:</span>
                        <span className="ml-1 text-gray-600">
                          {school.institution_type}
                        </span>
                      </div>
                      <div className="flex items-center">
                        <span className="font-medium text-gray-700">
                          Ownership:
                        </span>
                        <span className="ml-1 text-gray-600">
                          {school.ownership}
                        </span>
                      </div>
                      <div className="flex items-center">
                        <span className="font-medium text-gray-700">
                          Location:
                        </span>
                        <span className="ml-1 text-gray-600">
                          {school.parish && `${school.parish}, `}
                          {school.district}
                        </span>
                      </div>
                      {school.total_learners && (
                        <div className="flex items-center">
                          <span className="font-medium text-gray-700">
                            Learners:
                          </span>
                          <span className="ml-1 text-gray-600">
                            {school.total_learners}
                          </span>
                        </div>
                      )}
                      {school.total_teachers && (
                        <div className="flex items-center">
                          <span className="font-medium text-gray-700">
                            Teachers:
                          </span>
                          <span className="ml-1 text-gray-600">
                            {school.total_teachers}
                          </span>
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => setSelectedSchoolId(school.institution_id)}
                      className="mt-2 w-full bg-primary-500 text-white text-sm py-1 px-2 rounded hover:bg-primary-600 transition-colors"
                    >
                      View Details
                    </button>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MarkerClusterGroup>
        </MapContainer>
      </div>

      {/* Custom styles for map */}
      <style>{`
        .leaflet-container {
          font-size: 14px;
          font-family: Poppins, system-ui, -apple-system, sans-serif;
        }

        .leaflet-popup-content-wrapper {
          font-family: Poppins, system-ui, -apple-system, sans-serif;
        }

        .cluster-marker {
          background-color: #3b82f6;
          border-radius: 50%;
          width: 35px;
          height: 35px;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 2px solid white;
          box-shadow: 0 2px 4px rgba(0,0,0,0.3);
        }

        .cluster-count {
          color: white;
          font-weight: bold;
          font-size: 14px;
        }
      `}</style>

      {/* School Detail Modal */}
      {selectedSchoolId && selectedSchool && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-auto shadow-xl">
            {/* Modal Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 capitalize">
                  {selectedSchool.institution_name}
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  {selectedSchool.institution_type} • {selectedSchool.ownership}
                </p>
              </div>
              <button
                onClick={() => setSelectedSchoolId(null)}
                className="text-gray-400 hover:text-gray-600 text-2xl"
                aria-label="Close modal"
              >
                ×
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-6">
              {/* Enrollment Information */}
              {(selectedSchool.total_learners ||
                selectedSchool.boys_count ||
                selectedSchool.girls_count) && (
                <div className="bg-blue-50 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <span className="text-xl">📚</span>
                    Enrollment Information
                  </h3>
                  <div className="space-y-2 text-sm">
                    {selectedSchool.total_learners && (
                      <div className="flex items-start gap-2">
                        <span className="font-medium text-gray-700 min-w-[140px]">
                          Total Learners:
                        </span>
                        <span className="text-gray-900">
                          {selectedSchool.total_learners}
                        </span>
                      </div>
                    )}
                    {selectedSchool.boys_count && (
                      <div className="flex items-start gap-2">
                        <span className="font-medium text-gray-700 min-w-[140px]">
                          Boys:
                        </span>
                        <span className="text-gray-900">
                          {selectedSchool.boys_count}
                        </span>
                      </div>
                    )}
                    {selectedSchool.girls_count && (
                      <div className="flex items-start gap-2">
                        <span className="font-medium text-gray-700 min-w-[140px]">
                          Girls:
                        </span>
                        <span className="text-gray-900">
                          {selectedSchool.girls_count}
                        </span>
                      </div>
                    )}
                    {selectedSchool.disabled_learners && (
                      <div className="flex items-start gap-2">
                        <span className="font-medium text-gray-700 min-w-[140px]">
                          Learners with Disabilities:
                        </span>
                        <span className="text-gray-900">
                          {selectedSchool.disabled_learners}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Teacher Information */}
              {selectedSchool.total_teachers && (
                <div className="bg-emerald-50 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <span className="text-xl">👨‍🏫</span>
                    Teaching Staff
                  </h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-start gap-2">
                      <span className="font-medium text-gray-700 min-w-[140px]">
                        Total Teachers:
                      </span>
                      <span className="text-gray-900">
                        {selectedSchool.total_teachers}
                      </span>
                    </div>
                    {selectedSchool.male_teachers && (
                      <div className="flex items-start gap-2">
                        <span className="font-medium text-gray-700 min-w-[140px]">
                          Male Teachers:
                        </span>
                        <span className="text-gray-900">
                          {selectedSchool.male_teachers}
                        </span>
                      </div>
                    )}
                    {selectedSchool.female_teachers && (
                      <div className="flex items-start gap-2">
                        <span className="font-medium text-gray-700 min-w-[140px]">
                          Female Teachers:
                        </span>
                        <span className="text-gray-900">
                          {selectedSchool.female_teachers}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Location Details */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <span className="text-xl">📍</span>
                  Location Details
                </h3>
                <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
                  {selectedSchool.district && (
                    <div className="flex items-start gap-2">
                      <span className="font-medium text-gray-700 min-w-[100px]">
                        District:
                      </span>
                      <span className="text-gray-900">
                        {selectedSchool.district}
                      </span>
                    </div>
                  )}
                  {selectedSchool.subcounty && (
                    <div className="flex items-start gap-2">
                      <span className="font-medium text-gray-700 min-w-[100px]">
                        Subcounty:
                      </span>
                      <span className="text-gray-900">
                        {selectedSchool.subcounty}
                      </span>
                    </div>
                  )}
                  {selectedSchool.parish && (
                    <div className="flex items-start gap-2">
                      <span className="font-medium text-gray-700 min-w-[100px]">
                        Parish:
                      </span>
                      <span className="text-gray-900">
                        {selectedSchool.parish}
                      </span>
                    </div>
                  )}
                  {selectedSchool.village && (
                    <div className="flex items-start gap-2">
                      <span className="font-medium text-gray-700 min-w-[100px]">
                        Village:
                      </span>
                      <span className="text-gray-900">
                        {selectedSchool.village}
                      </span>
                    </div>
                  )}
                  {selectedSchool.latitude && selectedSchool.longitude && (
                    <div className="flex items-start gap-2">
                      <span className="font-medium text-gray-700 min-w-[100px]">
                        Coordinates:
                      </span>
                      <span className="text-gray-900">
                        {selectedSchool.latitude.toFixed(6)},{' '}
                        {selectedSchool.longitude.toFixed(6)}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4">
              <button
                onClick={() => setSelectedSchoolId(null)}
                className="w-full md:w-auto px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

EducationFacilityMap.propTypes = {
  facilities: PropTypes.arrayOf(
    PropTypes.shape({
      institution_id: PropTypes.string,
      institution_name: PropTypes.string,
      institution_type: PropTypes.string,
      ownership: PropTypes.string,
      latitude: PropTypes.number,
      longitude: PropTypes.number,
      district: PropTypes.string,
      subcounty: PropTypes.string,
      parish: PropTypes.string,
      village: PropTypes.string,
      total_learners: PropTypes.number,
      total_teachers: PropTypes.number,
      boys_count: PropTypes.number,
      girls_count: PropTypes.number,
      male_teachers: PropTypes.number,
      female_teachers: PropTypes.number,
      disabled_learners: PropTypes.number,
    })
  ).isRequired,
  location: PropTypes.shape({
    name: PropTypes.string.isRequired,
    code: PropTypes.string,
  }),
  title: PropTypes.string.isRequired,
}

export default EducationFacilityMap
